import { Box } from '@chakra-ui/react'
import { type ReactElement, useState } from 'react'

import useInterval from '@/client/hooks/useInterval'

export default function SessionTalkTimeGauge({
  learnerSpeakingPercentage,
  bootcampAveragePercentage,
  radius = 160,
  gaugeWidth = 30,
  textFontSize = '45',
}: {
  learnerSpeakingPercentage: number
  bootcampAveragePercentage?: number
  radius?: number
  gaugeWidth?: number
  textFontSize?: string
}): ReactElement {
  const [currentSpeakingPercentage, setCurrentSpeakingPercentage] = useState(0)

  const halfGaugeWidth = gaugeWidth / 2
  const innerRadius = radius - halfGaugeWidth
  const circumference = Math.PI * innerRadius
  const strokeDasharray = circumference.toFixed(2)
  const percentageUnfilled = (100 - currentSpeakingPercentage) / 100
  const strokeDashoffset = (percentageUnfilled * circumference).toFixed(2)

  useInterval(
    () => {
      setCurrentSpeakingPercentage(
        Math.min(
          learnerSpeakingPercentage,
          currentSpeakingPercentage + learnerSpeakingPercentage / 100
        )
      )
    },
    currentSpeakingPercentage >= learnerSpeakingPercentage ? null : 10,
    [currentSpeakingPercentage]
  )

  const getLineAtPercentage = (percentage: number, additionalWidth = 0) => {
    const percentageRadians = ((100 - percentage) / 100) * Math.PI
    const middleX = radius + innerRadius * Math.cos(percentageRadians)
    const middleY = radius - innerRadius * Math.sin(percentageRadians)

    const x1 = middleX + (halfGaugeWidth + additionalWidth) * Math.cos(percentageRadians)
    const y1 = middleY - (halfGaugeWidth + additionalWidth) * Math.sin(percentageRadians)

    const x2 = middleX - (halfGaugeWidth + additionalWidth) * Math.cos(percentageRadians)
    const y2 = middleY + (halfGaugeWidth + additionalWidth) * Math.sin(percentageRadians)

    return [x1, y1, x2, y2]
  }

  const markerLine =
    bootcampAveragePercentage == null ? null : getLineAtPercentage(bootcampAveragePercentage, 0)

  return (
    <Box position="relative" display="inline-block">
      <svg width={radius * 2 + 20} height={radius} viewBox={`-20 0 ${radius * 2} ${radius + 27}`}>
        <defs>
          <clipPath id="semicircle-clip">
            {/* Clipping area to create semicircles */}
            <rect x="0" y="0" width={radius * 2} height={radius} />
          </clipPath>
        </defs>
        {/* Background circle */}
        <circle
          cx={radius}
          cy={radius}
          r={innerRadius}
          fill="transparent"
          stroke="green.border"
          strokeWidth={gaugeWidth}
          clipPath="url(#semicircle-clip)"
        />
        {/* Filled gauge */}
        <circle
          cx={radius}
          cy={radius}
          r={innerRadius}
          fill="transparent"
          stroke="green.subtle"
          strokeWidth={gaugeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          clipPath="url(#semicircle-clip)"
        />
        {/* Labels */}
        <text
          x={radius}
          y={radius - 20}
          fill="fg"
          fontWeight={600}
          fontSize={textFontSize}
          textAnchor="middle"
        >
          {currentSpeakingPercentage.toFixed(0)}%
        </text>
        {/* Bootcamp marker */}
        {markerLine == null || bootcampAveragePercentage == null ? null : (
          <>
            <line
              x1={markerLine[0]}
              y1={markerLine[1]}
              x2={markerLine[2]}
              y2={markerLine[3]}
              stroke="fg"
              strokeWidth={3}
            />
            <text
              x={markerLine[0]}
              y={markerLine[1] - 25}
              fill="fg"
              fontWeight={600}
              fontSize="15"
              textAnchor="end"
            >
              Bootcamp
            </text>
            <text
              x={markerLine[0]}
              y={markerLine[1] - 8}
              fill="fg"
              fontWeight={600}
              fontSize="15"
              textAnchor="end"
            >
              Avg: {bootcampAveragePercentage}%
            </text>
          </>
        )}
        <text
          x={halfGaugeWidth}
          y={radius + 25}
          fill="fg"
          fontWeight={600}
          fontSize="20"
          textAnchor="middle"
        >
          Learners
        </text>
        <text
          x={2 * radius - halfGaugeWidth}
          y={radius + 25}
          fill="fg"
          fontWeight={600}
          fontSize="20"
          textAnchor="middle"
        >
          Tutor
        </text>
      </svg>
    </Box>
  )
}
